import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import {
  addMemory,
  adminReadRunTrace,
  bulkDeleteConversations,
  cancelRun,
  compareReports,
  createConversation,
  deleteConversation,
  deleteMemory,
  deleteReport,
  exportConversation,
  exportResult,
  listConversations,
  listMemory,
  listReports,
  rateAnswer,
  readConversationMessages,
  readMyAnalystAccess,
  readReport,
  readResult,
  readScopeOptions,
  readSharedConversation,
  readTiming,
  readUsage,
  recordAnswerSignal,
  saveReport,
  updateConversation,
  type BrowserSignal,
  type ConversationDetail,
  type ConversationFormat,
  type ConversationRead,
  type ConversationUpdate,
  type FeedbackWrite,
  type MemoryWrite,
  type ResultFormat,
  type ScopePins,
} from '@/client'
import { attachmentName, saveBlob } from '@/lib/download'

/** The history list's filters, as the sidebar sets them. */
export interface ConversationListFilters {
  search?: string
  tag?: string
  storeId?: number
}

/** The history pane loads this many conversations at a time ("Load more"). */
export const CONVERSATION_PAGE_SIZE = 50

/** While an answer is being written with the panel closed, its conversation is re-read this often. */
export const ACTIVITY_POLL_MS = 4000

/** Memory and saved reports are read in one page: the backend's largest. */
const ONE_PAGE = 100

/**
 * The AI Analyst's query keys (VentaHub's `['ai-analyst', …]` roots, without
 * the client id: the tenant is the token's). Defined once, so a mutation's
 * invalidation always matches what a query registered (CLAUDE.md §4.1).
 */
export const aiAnalystKeys = {
  all: ['ai-analyst'] as const,
  me: () => [...aiAnalystKeys.all, 'me'] as const,
  conversationLists: () => [...aiAnalystKeys.all, 'conversations'] as const,
  conversations: (filters: ConversationListFilters) =>
    [...aiAnalystKeys.conversationLists(), filters] as const,
  recentConversations: (limit: number) =>
    [...aiAnalystKeys.conversationLists(), 'recent', limit] as const,
  conversationDetails: () => [...aiAnalystKeys.all, 'conversation'] as const,
  conversation: (id: string) => [...aiAnalystKeys.conversationDetails(), id] as const,
  shared: (id: string) => [...aiAnalystKeys.all, 'shared', id] as const,
  scopeOptions: () => [...aiAnalystKeys.all, 'scope-options'] as const,
  timing: () => [...aiAnalystKeys.all, 'timing'] as const,
  usage: () => [...aiAnalystKeys.all, 'usage'] as const,
  memory: () => [...aiAnalystKeys.all, 'memory'] as const,
  reportLists: () => [...aiAnalystKeys.all, 'reports'] as const,
  report: (id: string) => [...aiAnalystKeys.all, 'report', id] as const,
  comparisons: () => [...aiAnalystKeys.all, 'compare'] as const,
  comparison: (leftId: string, rightId: string) =>
    [...aiAnalystKeys.comparisons(), leftId, rightId] as const,
  result: (id: string) => [...aiAnalystKeys.all, 'result', id] as const,
  trace: (runId: string) => [...aiAnalystKeys.all, 'trace', runId] as const,
}

/**
 * What the caller may do with the analyst — `GET /v1/ai-analyst/me`. Open to
 * everybody, so the shell can ask before deciding whether to show the
 * launcher; a grant changes at the next page load, like `useAccess`.
 */
export function useAnalystMe() {
  return useQuery({
    queryKey: aiAnalystKeys.me(),
    queryFn: async () => (await readMyAnalystAccess({ throwOnError: true })).data,
    staleTime: 5 * 60_000,
  })
}

/**
 * The history list, a page at a time. The search is debounced by the page,
 * so this asks once typing pauses, not once a keystroke.
 */
export function useConversations(filters: ConversationListFilters, enabled = true) {
  return useInfiniteQuery({
    queryKey: aiAnalystKeys.conversations(filters),
    queryFn: async ({ pageParam }) =>
      (
        await listConversations({
          query: {
            search: filters.search || undefined,
            tag: filters.tag,
            store_id: filters.storeId,
            page: pageParam,
            limit: CONVERSATION_PAGE_SIZE,
          },
          throwOnError: true,
        })
      ).data,
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.pagination.page < last.pagination.pages ? last.pagination.page + 1 : undefined,
    enabled,
  })
}

/** The panel's history dropdown: the latest `limit` conversations. */
export function useRecentConversations(limit: number, enabled = true) {
  return useQuery({
    queryKey: aiAnalystKeys.recentConversations(limit),
    queryFn: async () =>
      (await listConversations({ query: { page: 1, limit }, throwOnError: true })).data.items,
    enabled,
  })
}

/**
 * One conversation with its messages.
 *
 * `useChat` is seeded once from this, so it must be what the server holds
 * *now*, not what was cached when the conversation was created (empty) or
 * last opened (an answer shorter than the one since written). VentaHub set
 * `gcTime: 0` for that; here `refetchOnMount: 'always'` plus the caller
 * waiting for `isFetchedAfterMount` does the same job without throwing away
 * the cache entry — a zero gc time would also discard what `setQueryData`
 * writes (CLAUDE.md §6.3), which the rename and share mutations rely on.
 */
export function useConversation(id: string | null | undefined) {
  return useQuery({
    queryKey: aiAnalystKeys.conversation(id ?? ''),
    queryFn: () => readConversation(id ?? ''),
    enabled: Boolean(id),
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: false,
  })
}

async function readConversation(id: string) {
  return (await readConversationMessages({ path: { conversation_id: id }, throwOnError: true }))
    .data
}

/**
 * The answer being written in a conversation, if any (its `active_run_id`),
 * for the launcher's badges while the panel is closed. It reads the same
 * entry as `useConversation`, so opening the panel finds it warm, and re-reads
 * it every few seconds only while an answer is in progress.
 */
export function useConversationActivity(id: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: aiAnalystKeys.conversation(id ?? ''),
    queryFn: () => readConversation(id ?? ''),
    enabled: Boolean(id) && enabled,
    select: (detail: ConversationDetail) => detail.active_run_id ?? null,
    refetchInterval: (query) => (query.state.data?.active_run_id ? ACTIVITY_POLL_MS : false),
    refetchOnWindowFocus: false,
    retry: false,
  })
}

/** A conversation someone shared in the organization, read-only. */
export function useSharedConversation(id: string) {
  return useQuery({
    queryKey: aiAnalystKeys.shared(id),
    queryFn: async () =>
      (await readSharedConversation({ path: { conversation_id: id }, throwOnError: true })).data,
    retry: false,
  })
}

export function useScopeOptions(enabled = true) {
  return useQuery({
    queryKey: aiAnalystKeys.scopeOptions(),
    queryFn: async () => (await readScopeOptions({ throwOnError: true })).data,
    staleTime: 5 * 60_000,
    enabled,
  })
}

export function useAnalystTiming(enabled = true) {
  return useQuery({
    queryKey: aiAnalystKeys.timing(),
    queryFn: async () => (await readTiming({ throwOnError: true })).data,
    staleTime: 10 * 60_000,
    enabled,
  })
}

/** Today's allowance used. Refreshed when an answer starts and ends. */
export function useAnalystUsage(enabled = true) {
  return useQuery({
    queryKey: aiAnalystKeys.usage(),
    queryFn: async () => (await readUsage({ throwOnError: true })).data,
    staleTime: 30_000,
    enabled,
  })
}

/** Every list a finished or started answer changes: the history and the allowance. */
export function invalidateAfterAnswer(queryClient: QueryClient) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: aiAnalystKeys.conversationLists() }),
    queryClient.invalidateQueries({ queryKey: aiAnalystKeys.usage() }),
  ])
}

/** A new, empty conversation (the first question then goes to it). */
export function useCreateConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (scopePins: ScopePins) =>
      (await createConversation({ body: { scope_pins: scopePins }, throwOnError: true })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: aiAnalystKeys.conversationLists() }),
  })
}

/**
 * Rename, pin, share, tag or re-pin. The answer is the conversation as it now
 * stands, so it goes straight into the open detail.
 */
export function useUpdateConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: ConversationUpdate }) =>
      (
        await updateConversation({
          path: { conversation_id: id },
          body,
          throwOnError: true,
        })
      ).data,
    onSuccess: (conversation: ConversationRead) => {
      queryClient.setQueryData<ConversationDetail>(
        aiAnalystKeys.conversation(conversation.id),
        (detail) => (detail ? { ...detail, conversation } : detail),
      )
      return queryClient.invalidateQueries({ queryKey: aiAnalystKeys.conversationLists() })
    },
  })
}

export function useDeleteConversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteConversation({ path: { conversation_id: id }, throwOnError: true })
    },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: aiAnalystKeys.conversation(id) })
      return queryClient.invalidateQueries({ queryKey: aiAnalystKeys.conversationLists() })
    },
  })
}

export function useBulkDeleteConversations() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (ids: string[]) =>
      (await bulkDeleteConversations({ body: { ids }, throwOnError: true })).data.deleted,
    onSuccess: (_, ids) => {
      for (const id of ids) queryClient.removeQueries({ queryKey: aiAnalystKeys.conversation(id) })
      return queryClient.invalidateQueries({ queryKey: aiAnalystKeys.conversationLists() })
    },
  })
}

/** Download the whole conversation as Markdown or Word, under the server's file name. */
export function useExportConversation() {
  return useMutation({
    mutationFn: async ({ id, format }: { id: string; format: ConversationFormat }) => {
      const { data, response } = await exportConversation({
        path: { conversation_id: id },
        query: { format },
        parseAs: 'blob',
        throwOnError: true,
      })
      return {
        blob: data as Blob,
        filename: attachmentName(response, `ai-analyst-conversation.${format}`),
      }
    },
    onSuccess: ({ blob, filename }) => saveBlob(blob, filename),
  })
}

/** Download a stored result as CSV or Excel (every row, readable headers). */
export function useExportResult() {
  return useMutation({
    mutationFn: async ({ resultId, format }: { resultId: string; format: ResultFormat }) => {
      const { data, response } = await exportResult({
        path: { result_id: resultId },
        query: { format },
        parseAs: 'blob',
        throwOnError: true,
      })
      return {
        blob: data as Blob,
        filename: attachmentName(response, `ai-analyst-result.${format}`),
      }
    },
    onSuccess: ({ blob, filename }) => saveBlob(blob, filename),
  })
}

/** The query a stored result is read through (snapshot evidence, at most 200 rows). */
export function resultQuery(resultId: string) {
  return {
    queryKey: aiAnalystKeys.result(resultId),
    queryFn: async () =>
      (await readResult({ path: { result_id: resultId }, throwOnError: true })).data,
    staleTime: Infinity,
  }
}

/** Rate an answer (1, -1, or 0 to clear); the conversation's stored copy follows. */
export function useRateAnswer(conversationId: string | null | undefined) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ runId, body }: { runId: string; body: FeedbackWrite }) =>
      (await rateAnswer({ path: { run_id: runId }, body, throwOnError: true })).data,
    onSuccess: () =>
      conversationId
        ? queryClient.invalidateQueries({
            queryKey: aiAnalystKeys.conversation(conversationId),
            // The open chat is seeded from it once; the next open reads the new rating.
            refetchType: 'none',
          })
        : undefined,
  })
}

/** An implicit signal (dig deeper, copied, shared). Best effort: a lost one is not worth an error. */
export function useAnswerSignal() {
  return useMutation({
    mutationFn: async ({ runId, signal }: { runId: string; signal: BrowserSignal }) => {
      await recordAnswerSignal({ path: { run_id: runId }, body: { signal }, throwOnError: true })
    },
  })
}

/** Ask a run to stop; it stops at its next step and keeps what it wrote. */
export function useCancelRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (runId: string) =>
      (await cancelRun({ path: { run_id: runId }, throwOnError: true })).data,
    onSettled: () => invalidateAfterAnswer(queryClient),
  })
}

export function useMemory(enabled = true) {
  return useQuery({
    queryKey: aiAnalystKeys.memory(),
    queryFn: async () =>
      (await listMemory({ query: { limit: ONE_PAGE }, throwOnError: true })).data.items,
    enabled,
  })
}

export function useAddMemory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: MemoryWrite) => (await addMemory({ body, throwOnError: true })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: aiAnalystKeys.memory() }),
  })
}

export function useDeleteMemory() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteMemory({ path: { memory_id: id }, throwOnError: true })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: aiAnalystKeys.memory() }),
  })
}

/** Saved reports, without their snapshots (opening one reads it through `reportQuery`). */
export function useReports(enabled = true) {
  return useQuery({
    queryKey: aiAnalystKeys.reportLists(),
    queryFn: async () =>
      (await listReports({ query: { limit: ONE_PAGE }, throwOnError: true })).data.items,
    enabled,
  })
}

/** The query one saved report is read through, snapshot included. */
export function reportQuery(id: string) {
  return {
    queryKey: aiAnalystKeys.report(id),
    queryFn: async () => (await readReport({ path: { report_id: id }, throwOnError: true })).data,
    staleTime: Infinity,
  }
}

/**
 * Two saved reports side by side, `leftId` on the left, their insights
 * matched by area on the server (`GET /reports/compare`). A saved report
 * never changes, so neither does a comparison of two.
 */
export function useCompareReports(leftId: string, rightId: string) {
  return useQuery({
    queryKey: aiAnalystKeys.comparison(leftId, rightId),
    queryFn: async () =>
      (await compareReports({ query: { ids: `${leftId},${rightId}` }, throwOnError: true })).data,
    staleTime: Infinity,
    retry: false,
  })
}

/** Save a snapshot answer as a report. The answer is the report, so it is cached as one. */
export function useSaveReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: { run_id: string; title?: string | null }) =>
      (await saveReport({ body, throwOnError: true })).data,
    onSuccess: (report) => {
      queryClient.setQueryData(aiAnalystKeys.report(report.id), report)
      return queryClient.invalidateQueries({ queryKey: aiAnalystKeys.reportLists() })
    },
  })
}

export function useDeleteReport() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await deleteReport({ path: { report_id: id }, throwOnError: true })
    },
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: aiAnalystKeys.report(id) })
      queryClient.removeQueries({ queryKey: aiAnalystKeys.comparisons() })
      return queryClient.invalidateQueries({ queryKey: aiAnalystKeys.reportLists() })
    },
  })
}

/**
 * One finished answer's stored trace, for the reviewer-only Debug panel
 * (`GET /v1/ai-analyst/admin/runs/{id}`). Loaded only once the panel opens
 * (`enabled`), so a long conversation does not ask for every trace up front.
 */
export function useRunTrace(runId: string | null | undefined, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: aiAnalystKeys.trace(runId ?? ''),
    queryFn: async () =>
      (await adminReadRunTrace({ path: { run_id: runId ?? '' }, throwOnError: true })).data,
    enabled: Boolean(runId) && (options.enabled ?? true),
  })
}
